import React, { useEffect } from 'react';
import { Box, Button, Chip, Container, Grid, Paper, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import QuizIcon from '@mui/icons-material/Quiz';
import RouteIcon from '@mui/icons-material/Route';
import { Link } from 'react-router-dom';
import CredentialLogos from './CredentialLogos';

const learningSystem = [
  {
    icon: <MenuBookIcon />,
    title: 'Pattern Courses',
    text: 'Work through templates for sliding window, binary search, graphs, DP, heaps, and more.',
    to: '/courses',
  },
  {
    icon: <RouteIcon />,
    title: 'TopSWE100',
    text: 'A compact problem set organized by pattern, difficulty, and interview usefulness.',
    to: '/problems',
  },
  {
    icon: <QuizIcon />,
    title: 'Practice suite',
    text: 'Use quizzes, flashcards, and the roadmap as companion tools after the course lessons.',
    to: '/quizzes',
  },
];

const outcomes = [
  '20+ reusable coding patterns',
  '100 curated interview problems',
  'Practice suite for retention',
  'Roadmap built for weekly consistency',
];

const previewRows = [
  { pattern: 'Sliding Window', problem: 'Longest Substring', status: 'Template ready' },
  { pattern: 'Binary Search', problem: 'Minimum Capacity', status: 'Boundary notes' },
  { pattern: 'Graph BFS', problem: 'Shortest Path', status: 'Queue checklist' },
  { pattern: 'Dynamic Programming', problem: 'Coin Change', status: 'State diagram' },
];

function Home() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={{ bgcolor: 'background.default', color: 'text.primary' }}>
      <Box
        component="section"
        sx={{
          pt: { xs: 8, md: 12 },
          pb: { xs: 7, md: 10 },
          borderBottom: '1px solid',
          borderColor: 'divider',
          background: (theme) => theme.palette.custom.heroGradient,
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            right: { xs: '-42%', md: '-12%' },
            top: { xs: '8%', md: '2%' },
            width: { xs: 460, md: 760 },
            height: { xs: 460, md: 760 },
            background: 'linear-gradient(135deg, rgba(97, 111, 255, 0.42), rgba(115, 213, 255, 0.18), rgba(255,255,255,0))',
            filter: 'blur(22px)',
            transform: 'rotate(-18deg)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack spacing={4} alignItems={{ xs: 'center', md: 'flex-start' }} textAlign={{ xs: 'center', md: 'left' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box component="img" src="/topswe-logo.png" alt="TopSWE" sx={{ width: 44, height: 44, borderRadius: 2.5 }} />
                  <Typography sx={{ fontSize: '1.2rem', fontWeight: 850 }}>TopSWE</Typography>
                </Stack>
                <Chip
                  label="Coding interview prep, condensed"
                  sx={{
                    width: 'fit-content',
                    bgcolor: 'action.hover',
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: 'divider',
                    fontWeight: 600,
                  }}
                />
                <Box>
                  <Typography
                    variant="h1"
                    sx={{
                      maxWidth: 760,
                      fontSize: { xs: '3rem', sm: '4.2rem', md: '5.4rem' },
                      lineHeight: 0.95,
                      fontWeight: 800,
                      letterSpacing: 0,
                      mb: 3,
                    }}
                  >
                    Start solving with patterns.
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      maxWidth: 620,
                      color: 'text.secondary',
                      lineHeight: 1.45,
                      fontWeight: 400,
                    }}
                  >
                    Learn templates, run focused practice, and review with quizzes and flashcards in one calm workspace.
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  <Button
                    component={Link}
                    to="/courses"
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ px: 3, py: 1.4 }}
                  >
                    Start learning
                  </Button>
                  <Button
                    component={Link}
                    to="/problems"
                    variant="outlined"
                    size="large"
                    sx={{ px: 3, py: 1.4 }}
                  >
                    View TopSWE100
                  </Button>
                </Stack>
                <Stack direction="row" spacing={3} flexWrap="wrap" justifyContent={{ xs: 'center', md: 'flex-start' }} useFlexGap>
                  {[
                    ['3,500+', 'problems studied'],
                    ['20+', 'patterns'],
                    ['100', 'core problems'],
                  ].map(([value, label]) => (
                    <Box key={label}>
                      <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        {value}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {label}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <ProductPreview />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 4, md: 5 }, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', textAlign: 'center', mb: 3 }}>
            Created by a team who worked at
          </Typography>
          <CredentialLogos />
        </Container>
      </Box>

      <Container component="section" maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <SectionIntro
          eyebrow="The system"
          title="Everything points back to pattern recognition."
          text="The interface is intentionally quiet: pick a pattern, learn the template, practice the right problems, then reinforce the idea."
        />
        <Grid container spacing={2.5}>
          {learningSystem.map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item.title}>
              <Paper
                component={Link}
                to={item.to}
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'block',
                  color: 'text.primary',
                  textDecoration: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  transition: 'border-color 160ms ease, transform 160ms ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    borderColor: 'primary.main',
                  },
                }}
              >
                <Box sx={{ color: 'primary.main', mb: 2 }}>{item.icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                  {item.text}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box component="section" sx={{ borderY: '1px solid', borderColor: 'divider', background: 'linear-gradient(180deg, rgba(255,255,255,0.45), rgba(225, 248, 255, 0.34))' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 800 }}>
                Workflow
              </Typography>
              <Typography variant="h2" sx={{ fontSize: { xs: '2.35rem', md: '3.4rem' }, lineHeight: 1.05, fontWeight: 800, mt: 1, mb: 3 }}>
                A simpler way to practice every week.
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '1.08rem', lineHeight: 1.7, mb: 3 }}>
                Skip the scattered playlist. TopSWE keeps the loop small enough to repeat:
                learn a template, solve targeted problems, test recall, and move forward.
              </Typography>
              <Stack spacing={1.4}>
                {outcomes.map((item) => (
                  <Stack key={item} direction="row" spacing={1.2} alignItems="center">
                    <CheckCircleOutlineIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography>{item}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: 2,
                }}
              >
                {[
                  ['01', 'Learn', 'Read the pattern template and common signals.'],
                  ['02', 'Practice', 'Solve a focused set before switching topics.'],
                  ['03', 'Recall', 'Use quizzes and flashcards to catch gaps.'],
                  ['04', 'Review', 'Return to weak patterns with the roadmap.'],
                ].map(([step, title, text]) => (
                  <Paper
                    key={step}
                    elevation={0}
                    sx={{
                      p: 3,
                      minHeight: 170,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                      {step}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, my: 1 }}>
                      {title}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                      {text}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container component="section" maxWidth="md" sx={{ py: { xs: 8, md: 12 }, textAlign: 'center' }}>
        <Typography variant="h2" sx={{ fontSize: { xs: '2.4rem', md: '4rem' }, lineHeight: 1.02, fontWeight: 800, mb: 3 }}>
          Build the interview habit once.
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '1.12rem', lineHeight: 1.7, mb: 4 }}>
          Start with the curriculum, then use TopSWE100 and the practice suite to keep the system moving.
        </Typography>
        <Button component={Link} to="/signin" variant="contained" size="large" endIcon={<ArrowForwardIcon />} sx={{ px: 4, py: 1.5 }}>
          Create account
        </Button>
      </Container>
    </Box>
  );
}

function ProductPreview() {
  return (
    <Paper
      elevation={0}
      sx={{
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 24px 80px rgba(0,0,0,0.28)' : '0 30px 90px rgba(82, 107, 170, 0.2)',
      }}
    >
      <Box sx={{ px: 2.2, py: 1.6, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ff5f57' }} />
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ffbd2e' }} />
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#28c840' }} />
        <Typography variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
          TopSWE learning path
        </Typography>
      </Box>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 800 }}>
              This week
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5 }}>
              Pattern Sprint
            </Typography>
          </Box>
          {previewRows.map((row) => (
            <Box
              key={row.pattern}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1.2fr 1fr auto' },
                gap: 1.5,
                alignItems: 'center',
                p: 1.6,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.38)',
              }}
            >
              <Typography sx={{ fontWeight: 700 }}>{row.pattern}</Typography>
              <Typography sx={{ color: 'text.secondary' }}>{row.problem}</Typography>
              <Chip label={row.status} size="small" />
            </Box>
          ))}
          <Box
            sx={{
              mt: 1,
              p: 2,
              borderRadius: 2,
              bgcolor: 'action.hover',
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 800 }}>Roadmap progress</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Arrays, search, stacks, and graph basics</Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>
              64%
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
}

function SectionIntro({ eyebrow, title, text }) {
  return (
    <Box sx={{ maxWidth: 760, mb: 5 }}>
      <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 800 }}>
        {eyebrow}
      </Typography>
      <Typography variant="h2" sx={{ fontSize: { xs: '2.35rem', md: '3.4rem' }, lineHeight: 1.05, fontWeight: 800, mt: 1, mb: 2 }}>
        {title}
      </Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: '1.08rem', lineHeight: 1.7 }}>
        {text}
      </Typography>
    </Box>
  );
}

export default Home;
